import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { GetProcessApiResponse, GetProcessInterface } from '@interface/get-process-interface';
import { ProcessRouteData } from '@interface/process-route-guard.interface';
import { ProcessNavigationService } from '@services/process-route/process-navigation.service';
import { ProcessRouteContextService } from '@services/process-route/process-route-context.service';
import {
  PROCESS_ROUTE_DATA_KEY,
  PROCESS_VALIDATION_ERROR_MESSAGE,
} from '@services/process-route/process-route.constants';
import { ProcessRouteLoadingService } from '@services/process-route/process-route-loading.service';
import { ProcessSessionService } from '@services/process-route/process-session.service';
import { ProcessStatusApiService } from '@services/process-route/process-status-api.service';
import { ProcessStatusPolicyService } from '@services/process-route/process-status-policy.service';
import { RateChangingCheckService } from '@services/process-route/rate-changing-check.service';

@Injectable({
  providedIn: 'root',
})
export class ProcessRouteGuard implements CanActivate {
  private readonly processSession: ProcessSessionService = inject(ProcessSessionService);
  private readonly processStatusApi: ProcessStatusApiService = inject(ProcessStatusApiService);
  private readonly processStatusPolicy: ProcessStatusPolicyService = inject(ProcessStatusPolicyService);
  private readonly processNavigation: ProcessNavigationService = inject(ProcessNavigationService);
  private readonly processContext: ProcessRouteContextService = inject(ProcessRouteContextService);
  private readonly processRouteLoading: ProcessRouteLoadingService = inject(ProcessRouteLoadingService);
  private readonly rateChangingCheck: RateChangingCheckService = inject(RateChangingCheckService);
  private readonly router: Router = inject(Router);

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const idToken = this.processSession.getIdToken();

    if (!idToken || !this.processSession.hasValidIdToken()) {
      this.processContext.clear();
      this.processRouteLoading.hideWithDelay();
      this.processSession.clearInvalidSession();
      return this.processSession.buildLoginUrlTree(state.url);
    }

    const bypassProcessValidation = this.shouldBypassProcessValidation(route);

    if (bypassProcessValidation && !this.processSession.hasActiveProcess()) {
      this.processContext.clear();
      this.processRouteLoading.hideWithDelay();
      return true;
    }

    if (!this.processSession.hasActiveProcess()) {
      this.processContext.clear();
      this.processRouteLoading.hideWithDelay();
      return this.router.createUrlTree(['/mis-procesos']);
    }

    // No conservar una respuesta anterior durante la validación de una nueva navegación.
    this.processContext.clear();
    this.processRouteLoading.show();

    try {
      const processId = this.processSession.getIdProcess();
      const response = await this.processStatusApi.getProcess(processId, idToken);

      if (!this.isSuccessfulResponse(response)) {
        this.processNavigation.notifyValidationError(PROCESS_VALIDATION_ERROR_MESSAGE);
        return false;
      }

      this.processContext.publishFromGuard(processId, response.body);
      this.shareProcessWithRoute(route, response.body);

      if (bypassProcessValidation) {
        return true;
      }

      const routeData = this.getProcessRouteData(route, state.url);
      const decision = this.processStatusPolicy.evaluate(response.body, routeData);

      if (routeData.allowedDecisionTypes?.includes(decision.type)) {
        return true;
      }

      switch (decision.type) {
        case 'allow':
          return this.checkRateChangingOnAllow(response.body);
        case 'redirect-to-waiting':
          await this.processNavigation.redirectToWaiting();
          return false;
        case 'redirect-to-blocked':
          await this.processNavigation.redirectToBlocked();
          return false;
        case 'redirect-to-hard-block':
          await this.processNavigation.redirectToHardBlock();
          return false;
        case 'redirect-to-temporary-block':
          await this.processNavigation.redirectToTemporaryBlock();
          return false;
        case 'redirect-to-contact':
          await this.processNavigation.redirectToContact();
          return false;
        case 'redirect-to-not-found':
          await this.processNavigation.redirectToNotFound();
          return false;
        case 'redirect-to-step':
          await this.processNavigation.redirectToProcessStep(response.body);
          return false;
        default:
          this.processNavigation.notifyValidationError(PROCESS_VALIDATION_ERROR_MESSAGE);
          return false;
      }
    } catch {
      this.processNavigation.notifyValidationError(PROCESS_VALIDATION_ERROR_MESSAGE);
      return false;
    } finally {
      this.processRouteLoading.hideWithDelay();
    }
  }

  /**
   * Publica el proceso en `route.data` para que la vista destino lo lea por `ActivatedRoute`
   * en lugar de resolverlo otra vez.
   *
   * El router recalcula la `data` de esta ruta y de sus hijos después de los guards, así que
   * el valor también llega a los componentes cargados con `loadChildren`.
   */
  private shareProcessWithRoute(route: ActivatedRouteSnapshot, process: GetProcessInterface): void {
    route.data = { ...route.data, [PROCESS_ROUTE_DATA_KEY]: process };
  }

  private getProcessRouteData(route: ActivatedRouteSnapshot, currentUrl: string): ProcessRouteData {
    const hasTruoraCallback = this.hasTruoraCallback(route, currentUrl);
    const isIdentityValidationRoute = this.isIdentityValidationUrl(currentUrl);

    return {
      expectedStatuses: route.data['expectedStatuses'],
      allowedDecisionTypes: route.data['allowedDecisionTypes'],
      requireActiveProcess: route.data['requireActiveProcess'],
      requireMagicalExperience: route.data['requireMagicalExperience'],
      processStep: route.data['processStep'],
      isMagicalRoute: this.isMagicalExperienceUrl(currentUrl),
      // Solo en validar-identidad: evitar redirect mágico que borra el callback de Truora.
      hasTruoraCallback: hasTruoraCallback && isIdentityValidationRoute,
    };
  }

  private hasTruoraCallback(route: ActivatedRouteSnapshot, currentUrl: string): boolean {
    if (route.queryParamMap.has('call-back-truora')) {
      return true;
    }

    const queryIndex = currentUrl.indexOf('?');
    if (queryIndex === -1) {
      return false;
    }

    return new URLSearchParams(currentUrl.slice(queryIndex)).has('call-back-truora');
  }

  private isIdentityValidationUrl(url: string): boolean {
    const path = url.split('?')[0] || '';
    return path === '/validar-identidad' || path === '/magical_experience/validar-identidad';
  }

  private isMagicalExperienceUrl(url: string): boolean {
    const path = url.split('?')[0] || '';
    return path === '/magical_experience' || path.startsWith('/magical_experience/');
  }

  /**
   * Valida cambio de tasa al permitir la ruta.
   * Si hay `new_rate`, abre el modal; la navegación se permite y quien intente
   * una acción crítica (p. ej. pago) debe revalidar con el mismo servicio.
   */
  private async checkRateChangingOnAllow(process: GetProcessInterface): Promise<boolean> {
    const result = await this.rateChangingCheck.checkRateChanging(process);
    return result !== 'should_regenerate_documents';
  }

  private shouldBypassProcessValidation(route: ActivatedRouteSnapshot): boolean {
    return route.queryParamMap.get('force')?.toUpperCase() === 'OK';
  }

  private isSuccessfulResponse(response: GetProcessApiResponse): boolean {
    return [200, 201, 204].includes(response.status) && Boolean(response.body);
  }
}
